import React from 'react';
import { useSelector } from 'react-redux';
import { Theme, Divider, Box } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { OrderObject, Shopper, CurrencyObject } from 'common/types';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import * as ViewSelectors from 'selectors/ViewSelectors';
import {
	getVariantOrderProductsWithUnitsFromOrderProductList,
	getProductNameWithVariant,
} from 'common/utils/productUtils';
import { getPricingString } from 'common/utils/common';
import { getTranslation } from 'common/modules/translations';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import DialogContent from '@mui/material/DialogContent';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import PaymentsList from './PaymentsList';
import DeliveryPayment from './components/DeliveryPayment';
import { getResponsiblePersonDetails } from 'common/utils/newRentalUtils';
import classNames from 'classnames';
import { useTranslation } from 'services/localization/useTranslation';
import { getTotalProductPricing } from 'common/modules/orders';
import { PriceBreakdown } from 'common/components/molecules/PriceBreakdown';

interface OwnProps {
	open: boolean;
	rental: OrderObject;
	currency: CurrencyObject;
	handleClose: () => void;
}

type Props = OwnProps & WithStyles<typeof styles>;

const PaymentDialog = (props: Props) => {
	const { classes, open, rental, currency, handleClose } = props;
	const { t } = useTranslation();
	const { rentalInfo, shoppers, products, orderDelivery } = rental;
	const lang = useSelector(ViewSelectors.lang);
	const responsiblePersonDetails = getResponsiblePersonDetails(
		rentalInfo.responsiblePerson,
		shoppers,
	);
	const showSeparatePriceBreakdown = shoppers.length > 1 || !!orderDelivery;
	const renderShopperCards = (shoppers: Shopper[]) => {
		return shoppers.map((shopper, index) => {
			const shopperProducts = products.filter((p) => p.shopperId === shopper.id);
			const productsWithUnits = getVariantOrderProductsWithUnitsFromOrderProductList(
				shopperProducts,
			);
			const pricing = getTotalProductPricing(shopperProducts);
			return (
				<Paper key={shopper.id} elevation={1} className={classes.shopperCard}>
					<Typography variant="h6" gutterBottom data-sl="mask">
						{(shopper.firstName && shopper.firstName + ' ' + shopper.lastName) ||
							t('common:person.person') + ' ' + (index + 1)}
					</Typography>
					<List disablePadding>
						{productsWithUnits.map((product) => {
							const { units, variant, pricing } = product;
							const productName = getProductNameWithVariant(
								getTranslation(product.name, lang),
								variant || null,
								lang,
								product,
							);
							return (
								<React.Fragment key={product.id}>
									<ListItem className={classNames(classes.productItem, classes.font500)}>
										<Typography variant="body2">
											{units}
											{' x '}
											{productName}
										</Typography>
										<div className={classes.priceWrapper}>
											<Typography variant="body2">
												{getPricingString(pricing.listPrice, currency)}
											</Typography>
										</div>
									</ListItem>
									{!!pricing.deposit && (
										<ListItem className={classes.productItem}>
											<Typography variant="body2">
												{'+ '}
												{t('shoppingCart.deposit')}
											</Typography>
											<div className={classes.priceWrapper}>
												<Typography variant="caption">
													{getPricingString(pricing.deposit, currency)}
												</Typography>
											</div>
										</ListItem>
									)}
									<Box py={2}>
										<Divider />
									</Box>
								</React.Fragment>
							);
						})}
						<Box className={classes.pricingBreakdown}>
							<PriceBreakdown
								pricing={pricing}
								shopCurrency={currency}
								t={t}
								styling={{ totalDivider: true }}
								lang={lang}
							/>
						</Box>
					</List>
				</Paper>
			);
		});
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			scroll="paper"
			aria-labelledby="scroll-dialog-title"
			fullWidth
			fullScreen
			maxWidth={false}
		>
			<AppBar
				color="default"
				sx={{ backgroundColor: (theme) => theme.palette.background.paper }}
				position="relative"
				elevation={1}
			>
				<Toolbar>
					<IconButton
						data-qa="closeButton"
						color="inherit"
						onClick={handleClose}
						aria-label="Close"
						size="large"
					>
						<CloseIcon />
					</IconButton>
					<Typography color="inherit" sx={{ flexGrow: 1, fontSize: '1.8rem', fontWeight: 500 }}>
						{t('payment.summary', 'Payment summary')}
					</Typography>
				</Toolbar>
			</AppBar>
			<DialogContent className={classes.root}>
				<div className={classes.mainContainer}>
					{renderShopperCards(shoppers)}
					{rentalInfo.services?.delivery && orderDelivery && (
						<DeliveryPayment orderDelivery={orderDelivery} />
					)}
					{showSeparatePriceBreakdown && (
						<Box className={classes.paymentSummary}>
							<PriceBreakdown
								pricing={rentalInfo.pricing}
								shopCurrency={currency}
								t={t}
								styling={{ totalDivider: true, fontSize: 'large', showCombinedTaxLabels: false }}
								lang={lang}
							/>
						</Box>
					)}
				</div>
				<Paper square elevation={2} className={classes.sideBar}>
					<Typography variant="h5">{t('common:payment.paymentHistory')}</Typography>
					<PaymentsList
						shopId={rentalInfo.shopId}
						orderId={rentalInfo.id}
						currency={currency}
						userEmail={responsiblePersonDetails.email}
						userLanguage={responsiblePersonDetails.language}
					/>
				</Paper>
			</DialogContent>
		</Dialog>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		root: {
			display: 'flex',
			height: '100%',
			padding: 0,
		},
		mainContainer: {
			flex: '1 1 auto',
			background: theme.palette.background.default,
			padding: theme.spacing(2),
			paddingTop: 0,
			overflow: 'auto',
		},
		paymentRowContainer: {
			padding: '16px 0',
		},
		shopperCard: {
			margin: theme.spacing(2, 0),
			padding: theme.spacing(4),
		},
		sideBar: {
			flex: '1 0 25%',
			maxWidth: 325,
			minWidth: 250,
			backgroundColor: theme.palette.background.paper,
			zIndex: 2,
			padding: '8px 16px',
			position: 'relative',
			display: 'flex',
			flexDirection: 'column',
			overflow: 'auto',
			justifyContent: 'flex-start',
		},
		productItem: {
			justifyContent: 'space-between',
			alignItems: 'flex-start',
			padding: '2px 0',
		},
		priceWrapper: {
			flex: '0 0 auto',
			textAlign: 'right',
		},
		divider: {
			margin: '4px 0',
		},
		paymentSummary: {
			margin: theme.spacing(2, 4),
			maxWidth: 400,
			marginLeft: 'auto',
		},
		font500: {
			fontWeight: 500,
		},
		pricingBreakdown: {
			maxWidth: 400,
			marginLeft: 'auto',
		},
	});

export default withStyles(styles)(PaymentDialog);
